import React, { MouseEventHandler, ReactNode, useCallback, useState, useEffect, forwardRef, RefObject } from 'react'
import { Ripple, ButtonWrapper, IconWrapper } from './Button.css'
/**
 * none is 'ghost' in design
 * other option is same
 */
export type TOutline = 'none' | 'outline' | 'solid'
export type TButtonProps = {
    /**
     * Set the handler to handle `click` event
     * @param e
     */
    onClick?: MouseEventHandler<HTMLButtonElement>
    onMouseEnter?: MouseEventHandler<HTMLButtonElement>
    onMouseLeave?: MouseEventHandler<HTMLButtonElement>
    onMouseDown?: MouseEventHandler<HTMLButtonElement>
    /**
     * Can be set to `primary` `disabled` `outline` `default`
     */
    type?:
        | 'primary'
        | 'disabled'
        | 'outline'
        | 'default'
        | 'danger'
        | 'info'
        | 'white'
        | 'transparent'
        | 'highlight'
        | 'opacity'
        | 'ai'
    /**
     * Set the size of button
     */
    size?: 'lg' | 'md' | 'sm' | 'xs' | 'md-h' | 'md-up'
    outline?: TOutline
    isOutline?: boolean // @deprecated
    children?: ReactNode
    className?: string
    disabled?: boolean
    isRounded?: boolean
    isIcon?: boolean
    isLightMode?: boolean
    style?: any
    htmlType?: 'button' | 'submit'
    iconPosition?: 'left' | 'right'
    iconComponent?: ReactNode | null
    tabIndex?: number
    id?: string
    hasValueFilter?: boolean
    forResponsive?: boolean
}

interface ICoord {
    x: number
    y: number
}

const Button = (
    {
        type = 'default',
        onClick,
        onMouseEnter,
        onMouseLeave,
        onMouseDown,
        size = 'md',
        isOutline = false,
        outline = 'solid',
        children,
        style,
        className = '',
        htmlType = 'button',
        isRounded = false,
        isIcon = false,
        isLightMode = false,
        iconPosition = 'left',
        iconComponent = null,
        hasValueFilter = false,
        forResponsive,
        ...rest
    }: TButtonProps,
    refs: RefObject<HTMLButtonElement>
) => {
    const [coord, setCoord] = useState<ICoord>({ x: -1, y: -1 })
    const [isRippling, setRipping] = useState<boolean>(false)

    const typeClass = `btn-${type}`
    const sizeClass =
        size === 'lg'
            ? 'btn-lg'
            : size === 'md'
            ? 'btn-md'
            : size === 'md-h'
            ? 'btn-md-h'
            : size === 'sm'
            ? 'btn-sm'
            : 'btn-xs'

    const isClickable = type !== 'disabled' && !!onClick

    const handleOnClick = useCallback(
        (e) => {
            // TODO: temporary disable Ripple effect due to un-consistent between reactjs project vs vanilla project
            // const rect = e.target.getBoundingClientRect()
            // setCoord({ x: e.clientX - rect.left, y: e.clientY - rect.top })
            onClick && onClick(e)
        },
        [onClick]
    )

    useEffect(() => {
        if (coord.x !== -1 && coord.y !== -1) {
            setRipping(true)
            setTimeout(() => setRipping(false), 300)
        } else {
            setRipping(false)
        }
    }, [coord])

    useEffect(() => {
        if (!isRippling) setCoord({ x: -1, y: -1 })
    }, [isRippling])

    return (
        <ButtonWrapper
            type={htmlType}
            buttonClass={type}
            buttonSize={size}
            isRounded={isRounded}
            isIcon={isIcon}
            outline={outline}
            isLightMode={isLightMode}
            className={`${typeClass} ${sizeClass} ${className} ${isClickable ? 'clickable' : ''}`}
            onClick={handleOnClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            style={style}
            ref={refs}
            hasValueFilter={hasValueFilter}
            forResponsive={forResponsive}
            {...rest}
        >
            {iconComponent && iconPosition === 'left' && <IconWrapper isLeft>{iconComponent}</IconWrapper>}
            {children}
            {iconComponent && iconPosition === 'right' && <IconWrapper isLeft={false}>{iconComponent}</IconWrapper>}
            {isRippling ? <Ripple buttonClass={type} style={{ left: coord.x, top: coord.y }} /> : null}
        </ButtonWrapper>
    )
}

export default forwardRef<HTMLButtonElement, TButtonProps>(Button)
